import type { AccountCategory } from '../types/AccountCategory';
import type { CategoryItem } from 'src/shared/forms/components/CategoryBrowser';

/**
 * Build a tree structure from flat categories.
 *
 * Supports two data formats:
 * 1. Hierarchical path names: "Electronics > Mobile Phones > Smartphones"
 * 2. Parent ID references: parentCategoryId field
 *
 * This function builds a nested tree suitable for CategoryBrowser.
 */
export function buildCategoryTree(flatCategories: AccountCategory[]): CategoryItem[] {
  if (!flatCategories || flatCategories.length === 0) {
    return [];
  }

  // Check if categories use hierarchical path names (contain " > ")
  const hasPathNames = flatCategories.some(cat => cat.name.includes(' > '));

  if (hasPathNames) {
    return buildTreeFromPathNames(flatCategories);
  } else {
    return buildTreeFromParentIds(flatCategories);
  }
}

/**
 * Build tree from hierarchical path names (e.g., "Electronics > Mobile Phones")
 */
function buildTreeFromPathNames(flatCategories: AccountCategory[]): CategoryItem[] {
  const nodeMap = new Map<string, CategoryItem>();

  flatCategories.forEach(category => {
    const pathParts = category.name.split(' > ').map(part => part.trim());
    let currentPath = '';
    let parentNode: CategoryItem | null = null;

    pathParts.forEach((part, index) => {
      currentPath = currentPath ? `${currentPath} > ${part}` : part;
      const level = index + 1;
      const isLeaf = index === pathParts.length - 1;

      let node = nodeMap.get(currentPath);

      if (!node) {
        node = {
          id: isLeaf ? category.id : `path:${currentPath}`,
          name: part,
          level,
          children: [],
          path: pathParts.slice(0, index + 1),
        };
        nodeMap.set(currentPath, node);

        if (parentNode) {
          if (!parentNode.children?.find(child => child.id === node!.id)) {
            parentNode.children = parentNode.children || [];
            parentNode.children.push(node);
          }
        }
      }

      parentNode = node;
    });
  });

  const rootNodes: CategoryItem[] = [];
  nodeMap.forEach(node => {
    if (node.level === 1) {
      rootNodes.push(node);
    }
  });

  return sortNodes(rootNodes);
}

/**
 * Build tree from parentCategoryId references.
 *
 * Rules:
 * 1. Level 1 (Root): Categories with NO parentCategoryId
 * 2. Level 2+: Categories whose parentCategoryId matches a parent's id
 */
function buildTreeFromParentIds(flatCategories: AccountCategory[]): CategoryItem[] {
  // Create map of children by parent ID for efficient lookup
  const childrenByParentId = new Map<string, AccountCategory[]>();

  // Group categories by their parentCategoryId
  flatCategories.forEach(category => {
    const parentId = category.parentCategoryId || 'root';
    if (!childrenByParentId.has(parentId)) {
      childrenByParentId.set(parentId, []);
    }
    childrenByParentId.get(parentId)!.push(category);
  });

  // Get root categories (those without parentCategoryId)
  const rootCategories = childrenByParentId.get('root') || [];

  // Recursive function to build tree node with children
  const buildNode = (
    category: AccountCategory,
    level: number,
    parentPath: string[]
  ): CategoryItem => {
    const currentPath = [...parentPath, category.name];
    const children = childrenByParentId.get(category.id) || [];

    return {
      id: category.id,
      name: category.name,
      level,
      path: currentPath,
      children: children.length > 0
        ? children.map(child => buildNode(child, level + 1, currentPath))
        : [],
    };
  };

  // Build tree starting from root categories only
  const rootNodes = rootCategories.map(category => buildNode(category, 1, []));

  return sortNodes(rootNodes);
}

/**
 * Sort nodes alphabetically and recursively sort children
 */
function sortNodes(nodes: CategoryItem[]): CategoryItem[] {
  return nodes.sort((a, b) => a.name.localeCompare(b.name)).map(node => ({
    ...node,
    children: node.children && node.children.length > 0
      ? sortNodes(node.children)
      : undefined,
  }));
}

/**
 * Get the category ID from the selected path.
 * The ID is stored on the leaf (final) category in the path.
 */
export function getCategoryIdFromPath(categoryPath: CategoryItem[]): string {
  if (!categoryPath || categoryPath.length === 0) {
    return '';
  }

  // Get the last item in the path (the selected leaf category)
  const leafCategory = categoryPath[categoryPath.length - 1];
  return leafCategory.id;
}

/**
 * Find a category in the tree by its ID and return the path to it.
 */
export function findCategoryPath(tree: CategoryItem[], categoryId: string): CategoryItem[] {
  if (!categoryId || !tree || tree.length === 0) {
    return [];
  }

  const searchInTree = (nodes: CategoryItem[], path: CategoryItem[]): CategoryItem[] | null => {
    for (const node of nodes) {
      const currentPath = [...path, node];

      // Check if this is the target node
      if (node.id === categoryId) {
        return currentPath;
      }

      // Search in children
      if (node.children && node.children.length > 0) {
        const result = searchInTree(node.children, currentPath);
        if (result) {
          return result;
        }
      }
    }

    return null;
  };

  return searchInTree(tree, []) || [];
}

/**
 * Get the display name for a category path.
 * Returns the full hierarchical path as a string.
 */
export function getCategoryPathDisplayName(categoryPath: CategoryItem[]): string {
  if (!categoryPath || categoryPath.length === 0) {
    return '';
  }

  return categoryPath.map(item => item.name).join(' > ');
}
